import { afterEach, beforeEach, describe, expect, it, vi } from 'vitest';
import { MOLLIE_TEST_PROFILE_ID } from 'receivable-invoices-js/constants/mollie';

vi.mock('ember-window-mock', () => ({
  default: globalThis.window,
}));

const loadScriptMock = vi.fn().mockResolvedValue(undefined);

vi.mock('receivable-invoices-js/react/utils/load-script', () => ({
  loadScript: loadScriptMock,
}));

vi.mock('receivable-invoices-js/config/environment', () => ({
  default: {
    mollie: {
      testmode: true,
    },
  },
}));

const shouldSendToSentryMock = vi.fn().mockReturnValue(true);

vi.mock('receivable-invoices-js/react/utils/network', () => ({
  shouldSendToSentry: shouldSendToSentryMock,
}));

const { mollieService } = await import('./mollie');

describe('Mollie Service', () => {
  let captureException: ReturnType<typeof vi.fn>;

  beforeEach(() => {
    captureException = vi.fn();
    const win = window as unknown as Record<string, unknown>;
    if ('Mollie' in win) {
      delete win.Mollie;
    }

    const existingScript = document.querySelector('script#mollie-js');
    if (existingScript) {
      existingScript.remove();
    }

    mollieService.reset();
    vi.clearAllMocks();
  });

  afterEach(() => {
    vi.restoreAllMocks();
  });

  it('loads script and creates a new instance', async () => {
    const mockMollieInstance = {
      mount: vi.fn(),
      createComponent: vi.fn(),
      createToken: vi.fn(),
    };

    const win = window as unknown as Record<string, unknown>;
    // eslint-disable-next-line @vitest/prefer-spy-on -- Mollie doesn't exist on window initially
    win.Mollie = vi.fn(() => mockMollieInstance);

    // Create script element to simulate successful load
    const script = document.createElement('script');
    script.id = 'mollie-js';
    document.head.appendChild(script);

    await mollieService.load(MOLLIE_TEST_PROFILE_ID, 'en', captureException);

    expect(mollieService.isLoaded).toBe(true);
    expect(mollieService.mount).toBeDefined();
    expect(mollieService.createComponent).toBeDefined();
    expect(mollieService.createToken).toBeDefined();
  });

  it('sets hasError to true when script loading fails', async () => {
    const error = new Error('Failed to load');
    loadScriptMock.mockRejectedValueOnce(error);
    shouldSendToSentryMock.mockReturnValueOnce(true);

    await mollieService.load(MOLLIE_TEST_PROFILE_ID, 'en', captureException);

    expect(mollieService.hasError).toBe(true);
    expect(shouldSendToSentryMock).toHaveBeenCalledWith(error);
    expect(captureException).toHaveBeenCalledWith(error);
  });

  it('does not load multiple times', async () => {
    const mockMollieInstance = {
      mount: vi.fn(),
      createComponent: vi.fn(),
      createToken: vi.fn(),
    };

    const mollieSpy = vi.fn(() => mockMollieInstance);
    (window as unknown as Record<string, unknown>).Mollie = mollieSpy;

    // Create script element to simulate successful load
    const script = document.createElement('script');
    script.id = 'mollie-js';
    document.head.appendChild(script);

    await mollieService.load(MOLLIE_TEST_PROFILE_ID, 'en', captureException);
    await mollieService.load(MOLLIE_TEST_PROFILE_ID, 'en', captureException);

    expect(mollieSpy).toHaveBeenCalledTimes(1);
  });

  it('notifies subscribers when instance changes', async () => {
    const mockMollieInstance = {
      mount: vi.fn(),
      createComponent: vi.fn(),
      createToken: vi.fn(),
    };

    const win = window as unknown as Record<string, unknown>;
    // eslint-disable-next-line @vitest/prefer-spy-on -- Mollie doesn't exist on window initially
    win.Mollie = vi.fn(() => mockMollieInstance);

    // Create script element to simulate successful load
    const script = document.createElement('script');
    script.id = 'mollie-js';
    document.head.appendChild(script);

    const subscriber = vi.fn();
    mollieService.subscribe(subscriber);

    await mollieService.load(MOLLIE_TEST_PROFILE_ID, 'en', captureException);

    expect(subscriber).toHaveBeenCalledWith();
  });

  it('uses en_US locale when unsupported locale is provided', async () => {
    const mockMollieInstance = {
      mount: vi.fn(),
      createComponent: vi.fn(),
      createToken: vi.fn(),
    };

    const mollieSpy = vi.fn(() => mockMollieInstance);
    const win = window as unknown as Record<string, unknown>;

    win.Mollie = mollieSpy;

    // Create script element to simulate successful load
    const script = document.createElement('script');
    script.id = 'mollie-js';
    document.head.appendChild(script);

    await mollieService.load(MOLLIE_TEST_PROFILE_ID, 'unsupported-locale', captureException);

    expect(mollieSpy).toHaveBeenCalledWith(MOLLIE_TEST_PROFILE_ID, {
      testmode: true,
      locale: 'en_US',
    });
  });

  it('does not call captureException when shouldSendToSentry returns false', async () => {
    const error = new Error('Failed to load');
    loadScriptMock.mockRejectedValueOnce(error);
    shouldSendToSentryMock.mockReturnValueOnce(false);

    await mollieService.load(MOLLIE_TEST_PROFILE_ID, 'en', captureException);

    expect(mollieService.hasError).toBe(true);
    expect(shouldSendToSentryMock).toHaveBeenCalledWith(error);
    expect(captureException).not.toHaveBeenCalled();
  });

  it('prevents concurrent loads while loading is in progress', async () => {
    const mockMollieInstance = {
      mount: vi.fn(),
      createComponent: vi.fn(),
      createToken: vi.fn(),
    };

    const mollieSpy = vi.fn(() => mockMollieInstance);
    const win = window as unknown as Record<string, unknown>;

    win.Mollie = mollieSpy;

    // Create script element to simulate successful load
    const script = document.createElement('script');
    script.id = 'mollie-js';
    document.head.appendChild(script);

    // Make loadScript async and slow
    let resolveLoad!: () => void;
    const loadPromise = new Promise<void>(resolve => {
      resolveLoad = resolve;
    });
    loadScriptMock.mockReturnValueOnce(loadPromise);

    // Start first load (doesn't await)
    const firstLoad = mollieService.load(MOLLIE_TEST_PROFILE_ID, 'en', captureException);

    // Try to load again while first is in progress
    await mollieService.load(MOLLIE_TEST_PROFILE_ID, 'en', captureException);

    // Resolve the first load
    resolveLoad();
    await firstLoad;

    // Should only have been called once
    expect(mollieSpy).toHaveBeenCalledTimes(1);
  });

  it('returns false for isLoaded when script exists but no instance', () => {
    // Create script element
    const script = document.createElement('script');
    script.id = 'mollie-js';
    document.head.appendChild(script);

    // No instance has been created
    expect(mollieService.isLoaded).toBe(false);
  });

  it('returns false for isLoaded when instance exists but no script', async () => {
    const mockMollieInstance = {
      mount: vi.fn(),
      createComponent: vi.fn(),
      createToken: vi.fn(),
    };

    const win = window as unknown as Record<string, unknown>;
    // eslint-disable-next-line @vitest/prefer-spy-on -- Mollie doesn't exist on window initially
    win.Mollie = vi.fn(() => mockMollieInstance);

    // Load without creating script element
    await mollieService.load(MOLLIE_TEST_PROFILE_ID, 'en', captureException);

    expect(mollieService.isLoaded).toBe(false);
  });

  it('returns undefined for getters when instance is null', () => {
    expect(mollieService.mount).toBeUndefined();
    expect(mollieService.createComponent).toBeUndefined();
    expect(mollieService.createToken).toBeUndefined();
  });

  it('uses correct locale mapping for supported locales', async () => {
    const mockMollieInstance = {
      mount: vi.fn(),
      createComponent: vi.fn(),
      createToken: vi.fn(),
    };

    const mollieSpy = vi.fn(() => mockMollieInstance);
    const win = window as unknown as Record<string, unknown>;

    win.Mollie = mollieSpy;

    // Create script element to simulate successful load
    const script = document.createElement('script');
    script.id = 'mollie-js';
    document.head.appendChild(script);

    await mollieService.load(MOLLIE_TEST_PROFILE_ID, 'fr', captureException);

    expect(mollieSpy).toHaveBeenCalledWith(MOLLIE_TEST_PROFILE_ID, {
      testmode: true,
      locale: 'fr_FR',
    });
  });
});
